import React from 'react';

/**
 * Custom stylized brand-inspired SVG icons.
 * Each accepts `size` and inherits `color` via currentColor.
 * These are minimalist marks inspired by the brands, not exact logos.
 */

const Svg = ({ size = 20, children, viewBox = '0 0 24 24', strokeWidth = 1.8 }) => (
  <svg
    width={size}
    height={size}
    viewBox={viewBox}
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    xmlns="http://www.w3.org/2000/svg"
  >
    {children}
  </svg>
);

// Anthropic Claude — sunburst / radial asterisk (Anthropic's signature mark)
export const ClaudeIcon = ({ size = 20 }) => (
  <Svg size={size} strokeWidth={1.8}>
    <path d="M12 2 L12 22" />
    <path d="M2 12 L22 12" />
    <path d="M5 5 L19 19" />
    <path d="M19 5 L5 19" />
    <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" />
  </Svg>
);

// Google Gemini — 4-point star
export const GeminiIcon = ({ size = 20 }) => (
  <Svg size={size} strokeWidth={1.6}>
    <path d="M12 2 C12 7 14 10 22 12 C14 14 12 17 12 22 C12 17 10 14 2 12 C10 10 12 7 12 2 Z" fill="currentColor" stroke="none" />
  </Svg>
);

// Perplexity — infinity / nodes
export const PerplexityIcon = ({ size = 20 }) => (
  <Svg size={size} strokeWidth={2}>
    <circle cx="7" cy="12" r="3" />
    <circle cx="17" cy="12" r="3" />
    <path d="M7 9 L17 15" />
    <path d="M7 15 L17 9" />
  </Svg>
);

// Midjourney — sailboat/wave
export const MidjourneyIcon = ({ size = 20 }) => (
  <Svg size={size} strokeWidth={1.8}>
    <path d="M4 18 L20 18" />
    <path d="M6 18 L12 6 L12 18" />
    <path d="M12 10 L18 18" />
  </Svg>
);

// Lovable — heart with pulse
export const LovableIcon = ({ size = 20 }) => (
  <Svg size={size} strokeWidth={1.8}>
    <path d="M12 20 C7 16 3 13 3 9 C3 6.5 5 5 7 5 C9 5 10.5 6 12 8 C13.5 6 15 5 17 5 C19 5 21 6.5 21 9 C21 13 17 16 12 20 Z" fill="currentColor" fillOpacity="0.15" />
  </Svg>
);

// Replit — hex cube
export const ReplitIcon = ({ size = 20 }) => (
  <Svg size={size} strokeWidth={1.8}>
    <path d="M5 4 H12 V11 H5 Z" fill="currentColor" fillOpacity="0.2" />
    <path d="M12 11 H19 V18 H12 Z" fill="currentColor" fillOpacity="0.2" />
    <path d="M5 18 H12 V25" transform="translate(0 -7)" fill="currentColor" fillOpacity="0.2" />
  </Svg>
);

// Cursor — chevron arrow / pointer
export const CursorIcon = ({ size = 20 }) => (
  <Svg size={size} strokeWidth={2}>
    <path d="M5 3 L21 12 L13 13 L9 21 Z" fill="currentColor" fillOpacity="0.15" />
  </Svg>
);

// Krea — abstract diamond gradient (single tone)
export const KreaIcon = ({ size = 20 }) => (
  <Svg size={size} strokeWidth={1.8}>
    <path d="M12 3 L21 12 L12 21 L3 12 Z" />
    <path d="M8 12 L12 8 L16 12 L12 16 Z" fill="currentColor" fillOpacity="0.4" stroke="none" />
  </Svg>
);

// Gamma — G in a circle
export const GammaIcon = ({ size = 20 }) => (
  <Svg size={size} strokeWidth={1.8}>
    <circle cx="12" cy="12" r="9" />
    <path d="M15 9 A4 4 0 1 0 15 15 L15 12 L12 12" />
  </Svg>
);

// Runway — geometric R
export const RunwayIcon = ({ size = 20 }) => (
  <Svg size={size} strokeWidth={1.8}>
    <path d="M6 20 L6 4 L14 4 A4 4 0 0 1 14 12 L6 12" />
    <path d="M11 12 L17 20" />
  </Svg>
);

// Suno AI — audio waveform
export const SunoIcon = ({ size = 20 }) => (
  <Svg size={size} strokeWidth={2}>
    <path d="M4 12 L4 12" />
    <path d="M7 9 L7 15" />
    <path d="M10 6 L10 18" />
    <path d="M13 8 L13 16" />
    <path d="M16 10 L16 14" />
    <path d="M19 12 L19 12" />
  </Svg>
);

// V0 (Vercel v0) — solid triangle
export const V0Icon = ({ size = 20 }) => (
  <Svg size={size} strokeWidth={1.8}>
    <path d="M12 3 L22 20 L2 20 Z" fill="currentColor" stroke="none" />
  </Svg>
);

// GitHub — outline cat silhouette (very stylized)
export const GithubIcon = ({ size = 20 }) => (
  <Svg size={size} strokeWidth={1.8}>
    <path d="M9 21 C9 20 8.5 18 8 17.5 C6 17 4 16 4 13 C4 11.5 4.5 10 5.5 9 C5 8 5 6.5 5.5 5.5 C7 5.5 8 6 9 7 C10 6.5 11 6.5 12 6.5 C13 6.5 14 6.5 15 7 C16 6 17 5.5 18.5 5.5 C19 6.5 19 8 18.5 9 C19.5 10 20 11.5 20 13 C20 16 18 17 16 17.5 C15.5 18 15 20 15 21" />
  </Svg>
);

// GitHub Copilot — bot circle with orbit
export const CopilotIcon = ({ size = 20 }) => (
  <Svg size={size} strokeWidth={1.8}>
    <circle cx="12" cy="12" r="5" />
    <circle cx="12" cy="12" r="9" strokeDasharray="2 3" />
    <circle cx="10" cy="11" r="0.6" fill="currentColor" />
    <circle cx="14" cy="11" r="0.6" fill="currentColor" />
  </Svg>
);

// Figma — F with dots
export const FigmaIcon = ({ size = 20 }) => (
  <Svg size={size} strokeWidth={1.8}>
    <path d="M8 3 L14 3 A3 3 0 0 1 14 9 L8 9 Z" fill="currentColor" fillOpacity="0.2" />
    <path d="M8 9 L14 9 A3 3 0 0 1 14 15 L8 15 Z" fill="currentColor" fillOpacity="0.2" />
    <path d="M8 15 L11 15 A3 3 0 1 1 8 18 Z" fill="currentColor" fillOpacity="0.2" />
    <circle cx="17" cy="12" r="3" />
  </Svg>
);

// Vercel — triangle
export const VercelIcon = ({ size = 20 }) => (
  <Svg size={size} strokeWidth={1.5}>
    <path d="M12 4 L22 20 L2 20 Z" fill="currentColor" stroke="none" />
  </Svg>
);

// Discord — game controller
export const DiscordIcon = ({ size = 20 }) => (
  <Svg size={size} strokeWidth={1.8}>
    <path d="M6 8 C8 6 10 5.5 12 5.5 C14 5.5 16 6 18 8 L19 15 C18 16.5 16 17.5 14 18 L13 16 C13.5 16 14 15.5 14.5 15 C13 15.5 11 15.5 9.5 15 C10 15.5 10.5 16 11 16 L10 18 C8 17.5 6 16.5 5 15 Z" />
    <circle cx="9.5" cy="12" r="0.8" fill="currentColor" />
    <circle cx="14.5" cy="12" r="0.8" fill="currentColor" />
  </Svg>
);

// WhatsApp — chat bubble with phone
export const WhatsappIcon = ({ size = 20 }) => (
  <Svg size={size} strokeWidth={1.8}>
    <path d="M4 20 L5.5 15 A8 8 0 1 1 9 19 Z" />
    <path d="M9 10 C9 12.5 11.5 15 14 15" />
  </Svg>
);

// Instagram — square camera
export const InstagramIcon = ({ size = 20 }) => (
  <Svg size={size} strokeWidth={1.8}>
    <rect x="3" y="3" width="18" height="18" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17" cy="7" r="0.8" fill="currentColor" />
  </Svg>
);

// X / Twitter
export const XIcon = ({ size = 20 }) => (
  <Svg size={size} strokeWidth={2}>
    <path d="M4 4 L20 20" />
    <path d="M20 4 L4 20" />
  </Svg>
);

// Telegram — paper plane
export const TelegramIcon = ({ size = 20 }) => (
  <Svg size={size} strokeWidth={1.8}>
    <path d="M22 3 L2 11 L10 13 L13 21 Z" fill="currentColor" fillOpacity="0.15" />
    <path d="M10 13 L22 3" />
  </Svg>
);

// OpenRouter — layers
export const OpenRouterIcon = ({ size = 20 }) => (
  <Svg size={size} strokeWidth={1.8}>
    <path d="M12 3 L22 8 L12 13 L2 8 Z" />
    <path d="M2 12 L12 17 L22 12" />
    <path d="M2 16 L12 21 L22 16" />
  </Svg>
);

// Apple
export const AppleIcon = ({ size = 20 }) => (
  <Svg size={size} strokeWidth={1.8}>
    <path d="M12 6 C13 4.5 15 3 17 3 C17 5 15.5 6.5 14 7" />
    <path d="M8 8 C10 8 11 9 12 9 C13 9 14 8 16 8 C19 8 20 11 20 14 C20 17 18 21 15 21 C13.5 21 13 20 12 20 C11 20 10.5 21 9 21 C6 21 4 17 4 14 C4 11 5 8 8 8 Z" />
  </Svg>
);

// Windows / Microsoft — 4 squares
export const WindowsIcon = ({ size = 20 }) => (
  <Svg size={size} strokeWidth={1.6}>
    <rect x="3" y="3" width="8" height="8" fill="currentColor" fillOpacity="0.25" />
    <rect x="13" y="3" width="8" height="8" fill="currentColor" fillOpacity="0.25" />
    <rect x="3" y="13" width="8" height="8" fill="currentColor" fillOpacity="0.25" />
    <rect x="13" y="13" width="8" height="8" fill="currentColor" fillOpacity="0.25" />
  </Svg>
);
